import React, { useState, useRef, useEffect, useCallback, memo } from "react";
import {
  Box,
  Typography,
  Paper,
  InputBase,
  IconButton,
  Avatar,
  Skeleton,
  Tooltip,
} from "@mui/material";
import {
  Send as SendIcon,
  Cached as CachedIcon,
  Close as CloseIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { authSelector } from "../../services/redux/slices/authSlice";
import { AppDispatch, RootState } from "../../services/redux/store";
import {
  chatStatusConfig,
  currentSessionId,
  setCurrentSessionId,
} from "../../services/redux/slices/chatSlice";
import chatServices from "../../services/api/service/chatService";
import { decrypt } from "../../utils/crypto";
import { formatTimeAgo } from "../../utils/dateFormatUtils";

// --- CONFIGURATION ---
const DEFAULT_MESSAGE_LIMIT = 50;
const CUSTOMER_AVATAR_PLACEHOLDER = "/path/to/customer/avatar.png";

// --- INTERFACE DEFINITIONS ---
interface ChatMessage {
  id: string;
  message: string;
  source: "customer" | "sales";
  created_at: string;
  avatar?: string;
}
interface ConnectChatComponentProps {
  open: boolean;
  onClose: () => void;
  userName?: string;
}

const ChatMessageBubble: React.FC<{ message: ChatMessage }> = memo(
  ({ message }) => {
    const isCurrentUserIsSales = message?.source === "sales";
    const borderRadiusStyle = {
      borderRadius: "12px",
      ...(isCurrentUserIsSales ? { borderBottomRightRadius: 0 } : { borderBottomLeftRadius: 0 }),
    };

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: isCurrentUserIsSales ? "flex-end" : "flex-start",
          alignItems: "flex-end",
          mb: 2,
        }}
      >
        {!isCurrentUserIsSales && (
          <Avatar
            src={message.avatar || CUSTOMER_AVATAR_PLACEHOLDER}
            sx={{ width: 28, height: 28, mr: 1, mb: 1, flexShrink: 0 }}
          >
            {!message.avatar && <PersonIcon fontSize="small" />}
          </Avatar>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: isCurrentUserIsSales ? "flex-end" : "flex-start",
            maxWidth: "75%",
          }}
        >
          <Box
            sx={{
              p: 1.5,
              ...borderRadiusStyle,
              bgcolor: isCurrentUserIsSales ? "#E0F7FA" : "#F5F5F5",
              color: "#1F2233",
              boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                fontSize: "0.95rem",
                wordBreak: "break-word",
                lineHeight: 1.4,
              }}
            >
              {message?.message}
            </Typography>
          </Box>

          <Typography
            variant="caption"
            sx={{
              color: "#999",
              fontSize: "10px",
              mt: 0.5,
              px: 0.5,
            }}
          >
            {formatTimeAgo(message?.created_at)}
          </Typography>
        </Box>
      </Box>
    );
  }
);
const MemoizedChatMessageBubble = ChatMessageBubble;

const ConnectChatComponent: React.FC<ConnectChatComponentProps> = ({
  onClose,
  userName = "Sales Team"
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { userProfile } = useSelector(authSelector);
  const chatId = useSelector(currentSessionId);
  const { trackingId } = useSelector((state: RootState) => state.stepper);
  const [loadingChatMessages, setLoadingChatMessages] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState<string>("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [messageLimit, setMessageLimit] = useState(DEFAULT_MESSAGE_LIMIT);
  const { chatAccess } = useSelector(chatStatusConfig);

  const scrollToBottom = (behavior: "smooth" | "auto" = "smooth") => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  const fetchChatSessions = useCallback(async () => {
    if (!trackingId) return;
    try {
      const res = await chatServices.getAllChatSessions({ trackingId });
      const latestSessionId = res.data?.data?.[0]?.id || "";
      if (latestSessionId && latestSessionId !== chatId) {
        dispatch(setCurrentSessionId(latestSessionId));
      }
    } catch (error) {
      console.error("Failed to fetch sessions", error);
    }
  }, [trackingId, chatId, dispatch]);

  useEffect(() => {
    fetchChatSessions();
  }, [fetchChatSessions]);

  const fetchChatMessages = useCallback(
    async (chatIdOverride?: string) => {
      const idToFetch = chatIdOverride || chatId;
      if (!idToFetch) return;

      if (messageLimit > DEFAULT_MESSAGE_LIMIT) setLoadingChatMessages(true);
      try {
        const res = await chatServices.getMessagesForChat({
          chatId: idToFetch,
          limit: messageLimit,
        });
        const rawMessages = res.data?.data || [];
        const orderedMessages = [...rawMessages].reverse();
        setChatMessages(orderedMessages);
      } catch (error) {
        console.error("Failed to fetch messages", error);
      } finally {
        setLoadingChatMessages(false);
      }
    },
    [chatId, messageLimit]
  );

  useEffect(() => {
    let intervalId: any;
    if (chatId) {
      fetchChatMessages(chatId);
      if (messageLimit === DEFAULT_MESSAGE_LIMIT) {
        intervalId = setInterval(() => fetchChatMessages(chatId), 5000);
      }
    }
    return () => clearInterval(intervalId);
  }, [chatId, messageLimit, fetchChatMessages]);

  useEffect(() => {
    scrollToBottom("auto");
  }, [chatMessages.length]);

  const handleSendMessage = async () => {
    const messageToSend = newMessage?.trim();
    if (!messageToSend) return;

    const tempMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      message: messageToSend,
      source: "sales",
      created_at: new Date().toISOString(),
    };

    setChatMessages((prev) => [...prev, tempMessage]);
    setNewMessage("");

    try {
      if (!trackingId) {
        console.error("Missing trackingId, cannot send chat message.");
        return;
      }

      let activeChatId = chatId;

      // If there is no existing chat/session, create one first (only once)
      if (!activeChatId) {
        const createChatSessionRes = await chatServices.createChatSession({
          key:userProfile?.id,
          trackingId: trackingId,
          typeOfApplicant: "Applicant",
        });

        activeChatId = createChatSessionRes?.data?.data?.id;

        if (activeChatId) {
          dispatch(setCurrentSessionId(activeChatId));
        } else {
          console.error("Failed to create chat session: missing id");
          return;
        }
      }

      // Send message for the existing/newly created chat session
      await chatServices.sendMessage(activeChatId, {
        source: "sales",
        message: messageToSend,
      });

      // Refresh messages after successful send
      await fetchChatMessages(activeChatId);
    } catch (error) {
      console.error("Sales: Failed to send message:", error);
    }
  };

  const handleRefreshChat = () => {
    if (messageLimit !== DEFAULT_MESSAGE_LIMIT) {
      setMessageLimit(DEFAULT_MESSAGE_LIMIT);
    } else if (chatId) {
      fetchChatMessages(chatId);
    } else {
      fetchChatSessions();
    }
    scrollToBottom("smooth");
  };

  return (
    <Paper
      elevation={10}
      sx={{
        position: "fixed",
        bottom: { xs: 16, md: 24 },
        right: { xs: 16, md: 24 },
        zIndex: 9999,
        width: { xs: "320px", sm: "360px" },
        height: { xs: "480px", sm: "500px" },
        maxWidth: "calc(100vw - 32px)",
        maxHeight: "calc(100vh - 32px)",
        borderRadius: "16px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "#fff",
        boxShadow: "0px 8px 30px rgba(0,0,0,0.15)",
      }}
    >
      {/* HEADER */}
      <Box sx={{ py: 1.5, px: 2, bgcolor: "#FEC10D", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "16px", color: "#383838" }}>Chat Support (Sales)</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh Chat">
            <IconButton onClick={handleRefreshChat} disabled={loadingChatMessages} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.3)' }}>
              <CachedIcon fontSize="small" sx={{ color: '#333' }} />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} size="small" sx={{ bgcolor: "white" }}>
            <CloseIcon fontSize="small" sx={{ color: "black" }} />
          </IconButton>
        </Box>
      </Box>

      {/* BODY */}
      <Box
        ref={chatContainerRef}
        sx={{
          flexGrow: 1,
          px: 2,
          pt: 2,
          pb: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#fff",
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "#00B3B533", borderRadius: "10px" },
        }}
      >
        {chatMessages?.length > 0 ? (
          <>
            {chatMessages.map((message) => (
              <MemoizedChatMessageBubble key={message.id} message={message} />
            ))}
            <div ref={messagesEndRef} />
          </>
        ) : (
          <Box sx={{ m: "auto", textAlign: "center", opacity: 0.6 }}>
            <Typography variant="body2">No messages yet.</Typography>
          </Box>
        )}
      </Box>

      {/* FOOTER */}
      <Box sx={{ p: 1.5, bgcolor: "#f5fbfb", borderTop: "1px solid #eee" }}>
        <Paper
          elevation={0}
          component="form"
          onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }}
          sx={{ display: "flex", alignItems: "center", borderRadius: "24px", border: "1px solid #C9E3E6", px: 1, bgcolor: "#fff" }}
        >
          <InputBase
            placeholder="Reply to customer..."
            fullWidth
            multiline
            maxRows={2}
            // disabled={!chatAccess || !chatId}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onFocus={() => setTimeout(() => scrollToBottom("smooth"), 300)}
            sx={{ py: 1, px: 1.5, fontSize: "0.9rem" }}
          />
          <IconButton type="submit"
            // disabled={!newMessage.trim() || !chatAccess || !chatId}
            sx={{ color: "#168794", p: 1 }}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </Paper>
      </Box>
    </Paper>
  );
};

export default ConnectChatComponent;
